import { useTranslation } from 'react-i18next';
import { ToggleButton, ToggleButtonGroup, Paper } from '@mui/material';
import {
  Article as ArticleIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';

import { useCaseStudiesLayer } from '../../map-data/CaseStudiesLayerProvider';

type LayerMode = 'studies' | 'case-studies';

export function LayerToggle() {
  const { t } = useTranslation();
  const { showCaseStudiesLayer, setCaseStudiesLayerVisible } =
    useCaseStudiesLayer();

  const currentMode: LayerMode = showCaseStudiesLayer
    ? 'case-studies'
    : 'studies';

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: LayerMode | null,
  ) => {
    if (newMode !== null) {
      setCaseStudiesLayerVisible(newMode === 'case-studies');
    }
  };

  return (
    <Paper
      elevation={0}
      data-tour="layer-toggle"
      sx={{
        position: 'absolute',
        bottom: 38,
        left: 58,
        zIndex: 1000,
        backgroundColor: 'background.paper',
      }}
    >
      <ToggleButtonGroup
        value={currentMode}
        exclusive
        onChange={handleModeChange}
        aria-label="map layer selection"
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: 1,
            px: 2,
            py: 1,
            fontSize: '0.75rem',
            fontWeight: 500,
            gap: 0.5,
            minWidth: 'auto',
          },
          '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          },
          '& .MuiToggleButton-root:not(.Mui-selected)': {
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
        }}
      >
        <ToggleButton value="studies" aria-label="Show studies">
          <LocationIcon sx={{ fontSize: 16 }} />
          {t('pinMap.layers.studies', 'Studies')}
        </ToggleButton>
        <ToggleButton value="case-studies" aria-label="Show case studies">
          <ArticleIcon sx={{ fontSize: 16 }} />
          {t('pinMap.layers.caseStudies', 'Case studies')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Paper>
  );
}
